// ─── Fiche d'une période de vacances (info + édition) ────────────
// Standard app-wide : tap sur un élément = fiche INFO en lecture seule, avec ✏️
// (éditer) et 🗑️ (supprimer) en en-tête. ✏️ bascule en mode édition (nom + dates),
// 💾 enregistre. Utilisée par FeuilleVacances.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, StyleSheet, TouchableOpacity } from 'react-native';
import { useUI, useT, Feuille, SelecteurDate, confirmerSuppression, frJMA, HIT } from '@mop/core';
import { emojiVacances, calculerMoities } from './services/vacances';

const fmtFRan = frJMA;   // JJ/MM/AAAA (Core)
const nbJours = (debut, fin) => {
  if (!debut || !fin) return 0;
  const a = new Date(debut + 'T00:00:00'), b = new Date(fin + 'T00:00:00');
  return Math.max(0, Math.round((b - a) / 86400000) + 1);
};
const dateValide = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s || '');

export default function FeuilleFichePeriode({ visible, periode, onFermer, onEnregistrer, onSupprimer }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [mode, setMode] = useState('info');
  const [nom, setNom] = useState('');
  const [debut, setDebut] = useState('');
  const [fin, setFin] = useState('');
  const [pickD, setPickD] = useState(null);   // 'debut' | 'fin' | null

  useEffect(() => {
    if (!visible) return;
    setMode('info');
    setNom(periode?.nom || '');
    setDebut(periode?.debut || '');
    setFin(periode?.fin || '');
  }, [visible, periode]);

  if (!periode) return null;

  const valide = nom.trim() && dateValide(debut) && dateValide(fin) && debut <= fin;
  const modifie = nom.trim() !== (periode.nom || '') || debut !== periode.debut || fin !== periode.fin;
  const m = dateValide(debut) && dateValide(fin) && debut <= fin ? calculerMoities(debut, fin) : null;

  function enregistrer() {
    if (!valide || !modifie) return;
    onEnregistrer?.({ nom: nom.trim(), debut, fin });
    setMode('info');
  }

  // En-tête : mode info → ✏️ + 🗑️ ; mode édition → 💾 (via action).
  const action = mode === 'edit'
    ? { icone: '💾', onPress: enregistrer, disabled: !valide || !modifie, label: t('commun.enregistrer') }
    : null;
  const boutons = mode === 'info' ? (
    <>
      <TouchableOpacity onPress={() => setMode('edit')} hitSlop={HIT} accessibilityLabel={t('commun.editer')}><Text style={s.hIc}>✏️</Text></TouchableOpacity>
      <TouchableOpacity onPress={() => confirmerSuppression(t('vacances.supprimerTitre'), t('vacances.periodeRetireeMsg', { nom: periode.nom }), () => { onSupprimer?.(); onFermer?.(); })} hitSlop={HIT} accessibilityLabel={t('commun.supprimer')}><Text style={s.hIc}>🗑️</Text></TouchableOpacity>
    </>
  ) : null;

  return (
   <>
    <Feuille visible={visible} titre={mode === 'edit' ? t('vacances.modifierPeriode') : t('vacances.fichePeriode')}
      emoji={emojiVacances(periode.nom)} hauteur={0.82} onFermer={onFermer} action={action} boutons={boutons}>
      {mode === 'info' ? (
        <View>
          <Text style={s.nom}>{periode.nom}</Text>
          <View style={s.champInfo}><Text style={s.lblInfo}>{t('vacances.du')}</Text><Text style={s.valInfo}>{fmtFRan(periode.debut)}</Text></View>
          <View style={s.champInfo}><Text style={s.lblInfo}>{t('vacances.au')}</Text><Text style={s.valInfo}>{fmtFRan(periode.fin)}</Text></View>
          <View style={s.champInfo}><Text style={s.lblInfo}>{t('finances.duree')}</Text><Text style={s.valInfo}>{t('vacances.nJours', { n: nbJours(periode.debut, periode.fin) })}</Text></View>
          {m && (
            <View style={s.moities}>
              <Text style={s.moitiesTitre}>{t('vacances.repartitionMoities')}</Text>
              <Text style={s.moitiesTxt}>{t('vacances.moitie1Ligne', { debut: fmtFRan(m.debut1), fin: fmtFRan(m.fin1), n: m.nbJours1 })}</Text>
              <Text style={s.moitiesTxt}>{t('vacances.moitie2Ligne', { debut: fmtFRan(m.debut2), fin: fmtFRan(m.fin2), n: m.nbJours2 })}</Text>
              <Text style={s.aide}>{t('vacances.gardeMoitiesNote')}</Text>
            </View>
          )}
        </View>
      ) : (
        <View>
          <Text style={s.lbl}>{t('listes.nom')}</Text>
          <TextInput style={s.input} value={nom} onChangeText={setNom} placeholder={t('vacances.phNomPeriode')} placeholderTextColor={UI.faint} />
          <Text style={s.lbl}>{t('finances.debut')}</Text>
          <TouchableOpacity style={s.champBtn} onPress={() => setPickD('debut')}>
            <Text style={[s.champVal, !debut && s.champVide]}>📅 {debut ? fmtFRan(debut) : t('ft.choisir')}</Text>
          </TouchableOpacity>
          <Text style={s.lbl}>{t('finances.fin')}</Text>
          <TouchableOpacity style={s.champBtn} onPress={() => setPickD('fin')}>
            <Text style={[s.champVal, !fin && s.champVide]}>📅 {fin ? fmtFRan(fin) : t('ft.choisir')}</Text>
          </TouchableOpacity>
          {!valide && (nom || debut || fin) ? <Text style={s.err}>{t('vacances.erreurNomDates')}</Text> : null}
        </View>
      )}
    </Feuille>
    <SelecteurDate visible={!!pickD} titre={pickD === 'fin' ? t('finances.fin') : t('finances.debut')}
      valeur={pickD === 'fin' ? fin : debut}
      min={pickD === 'fin' ? debut : undefined}
      onChange={(dk) => (pickD === 'fin' ? setFin(dk) : setDebut(dk))}
      onFermer={() => setPickD(null)} />
   </>
  );
}


const makeStyles = (UI) => StyleSheet.create({
  hIc: { fontSize: 17 },
  nom: { fontSize: 18, fontWeight: '800', color: UI.ink, marginBottom: 12 },
  champInfo: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 9, borderBottomWidth: 0.5, borderBottomColor: UI.border },
  lblInfo: { fontSize: 13, color: UI.muted, fontWeight: '600' },
  valInfo: { fontSize: 14, color: UI.ink, fontWeight: '700' },
  moities: { marginTop: 14, backgroundColor: UI.card, borderRadius: 12, borderWidth: 1, borderColor: UI.border, padding: 12 },
  moitiesTitre: { fontSize: 12.5, fontWeight: '800', color: UI.ink, marginBottom: 6 },
  moitiesTxt: { fontSize: 12.5, color: UI.muted, marginTop: 2 },
  aide: { fontSize: 11.5, color: UI.faint, marginTop: 8, lineHeight: 16 },
  lbl: { fontSize: 12, fontWeight: '700', color: UI.muted, marginTop: 12, marginBottom: 7 },
  input: { backgroundColor: UI.champ, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 11, fontSize: 15, color: UI.ink, borderWidth: 0.5, borderColor: UI.champBord },
  champBtn: { backgroundColor: UI.champ, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 12, borderWidth: 0.5, borderColor: UI.champBord },
  champVal: { fontSize: 15, color: UI.ink, fontWeight: '600' },
  champVide: { color: UI.faint, fontWeight: '400' },
  err: { fontSize: 12, color: '#C62828', marginTop: 10 },
});
